import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Chip, Link, Tooltip, Autocomplete, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';

import { DataTable } from '../components/DataTable';
import { LocationForm } from '../components/locations/LocationForm';
import { locationsApi, locationAssignmentsApi, organizationsApi, devicesApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useDebounce } from '../hooks/useDebounce';
import { useDigestSettings } from '../hooks/useDigestSettings';
import { API_CONFIG, ENV } from '../api/config';
import { extractApiError } from '../utils/apiErrors';
import { onboardingDisplay, onboardingMuiColor } from '../utils/onboarding';
import type { Location, CreateLocationData } from '../api/config';

export const LocationsPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<Location | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganizationId = searchParams.get('organization_id');
  const [nameFilter, setNameFilter] = React.useState('');

  const queryClient = useQueryClient();
  // Global restock digest toggle (sc-6504): with restock off globally no location
  // auto-quotes. A failed load leaves the column as before (location-level only).
  const { data: digestSettings } = useDigestSettings();
  const restockGloballyOn = digestSettings?.types.restock?.enabled !== false;

  const debouncedNameFilter = useDebounce(nameFilter, 300);

  const locationFilters = React.useMemo(() => ({
    organization_id: selectedOrganizationId || undefined,
    name: debouncedNameFilter || undefined,
  }), [selectedOrganizationId, debouncedNameFilter]);

  const {
    rows: locations,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['locations', selectedOrganizationId, debouncedNameFilter],
    queryFn: (f, p) => locationsApi.getPage(f, p),
    filters: locationFilters,
    defaultSort: { field: 'name', sort: 'asc' },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
  });

  // Fetch assignment counts for all locations in a single request
  const { data: assignmentCounts = {} } = useQuery({
    queryKey: ['location_assignment_counts'],
    queryFn: () => locationAssignmentsApi.getCountsByLocation(),
  });

  const createMutation = useMutation({
    mutationFn: locationsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSnackbar({ message: 'Location created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create location'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: CreateLocationData }) => {
      const { assign_device_ids, ...locationData } = data;
      const updatedLocation = await locationsApi.update(id, locationData);
      if (assign_device_ids?.length) {
        await Promise.all(assign_device_ids.map(deviceId =>
          devicesApi.update(deviceId, { location_id: id as number })
        ));
      }
      return updatedLocation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setSnackbar({ message: 'Location updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update location'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: locationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSnackbar({ message: 'Location deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete location'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 35, resizable: true },
    {
      field: 'active',
      headerName: 'Status',
      width: 85,
      type: 'boolean',
      valueGetter: (params: GridRenderCellParams<Location>) => {
        return params.row?.active ?? false;
      },
      resizable: true,
    },
    {
      field: 'alerts_enabled',
      headerName: 'Alerts',
      width: 85,
      type: 'boolean',
      valueGetter: (params: GridRenderCellParams<Location>) => {
        return params.row?.active ? (params.row?.alerts_enabled ?? true) : false;
      },
      resizable: true,
    },
    {
      field: 'auto_quote',
      renderHeader: () => (
        <Tooltip title={
          <Box sx={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
            <strong>Auto-quote readiness checklist:</strong>{'\n\n'}
            Checked in this column:{'\n'}
            1. HexaHQ Account ID linked{'\n'}
            2. Account manager email set{'\n'}
            3. Restock enabled (on the location and globally){'\n\n'}
            Also required (not checked here):{'\n'}
            4. Products assigned with max counts{'\n'}
            5. Product equivalencies have HexaHQ SKUs{'\n'}
            6. HexaHQ account flagged as Edge customer{'\n'}
            7. Account manager has HexaHQ login{'\n'}
            8. Products exist and active in HexaHQ{'\n'}
            9. Pricing set up in HexaHQ
          </Box>
        }>
          <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>Auto-Quote</span>
        </Tooltip>
      ),
      width: 95,
      type: 'boolean',
      valueGetter: (params: GridRenderCellParams<Location>) => {
        const row = params.row;
        if (!row?.active) return false;
        const prefs = row.digest_preferences;
        return !!row.hexahq_account_id
          && !!prefs?.account_manager_email?.trim()
          && !!prefs?.restock
          && restockGloballyOn;
      },
      resizable: true,
    },
    {
      field: 'onboarding_state',
      headerName: 'Onboarding',
      width: 130,
      // Server-side sort/filter only allow specific columns; onboarding_state
      // isn't one, so don't offer them here (avoids a no-op server round-trip).
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Location>) => {
        const d = onboardingDisplay(params.row?.onboarding_state ?? null);
        return (
          <Tooltip title={d.description}>
            <Chip
              label={d.label}
              color={onboardingMuiColor(d.tone)}
              size="small"
              variant="outlined"
              // Mute the N/A case so it reads as "no data", not a real status.
              sx={d.tone === 'na' ? { color: 'text.disabled', borderColor: 'divider' } : undefined}
            />
          </Tooltip>
        );
      },
      resizable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 350,
      renderCell: (params: GridRenderCellParams<Location>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link
            component={RouterLink}
            to={`/locations/${params.row?.id}/dashboard`}
            sx={{ textDecoration: 'none' }}
          >
            {params.row?.name}
          </Link>
          {params.row?.internal && (
            <Tooltip title="HEXA-internal facility (warehouse/tagging site)">
              <Chip label="Internal" size="small" variant="outlined" color="info" />
            </Tooltip>
          )}
        </Box>
      ),
      resizable: true,
    },
    { field: 'slug', headerName: 'Slug', width: 120, resizable: true },
    {
      field: 'mesh_name',
      headerName: 'Mesh',
      width: 140,
      // Not in the server-side sort/filter whitelists (like onboarding_state).
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Location>) => (
        params.row?.mesh_name
          ? <Chip label={params.row.mesh_name} size="small" variant="outlined" color="primary" />
          : <Typography variant="body2" color="text.secondary">—</Typography>
      ),
      resizable: true,
    },
    {
      field: 'devices',
      headerName: 'Devices',
      filterable: false,
      width: 300,
      renderCell: (params: GridRenderCellParams<Location>) => {
        const devices = params.row?.devices || [];
        if (devices.length === 0) {
          return <Typography variant="body2" color="text.secondary">No devices</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            {devices.map((d: { id: number; device_id: string; power_configuration: string }) => (
              <Typography key={d.id} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {d.device_id} ({d.power_configuration})
              </Typography>
            ))}
          </Box>
        );
      },
      resizable: true,
    },
    {
      field: 'dashboard',
      headerName: 'Dashboard',
      filterable: false,
      width: 150,
      renderCell: (params: GridRenderCellParams<Location>) => {
        const slug = params.row?.slug;
        if (!slug) return null;

        // The public report deliberately 404s for internal locations (sc-5719),
        // so don't render a dead link (sc-6110).
        if (params.row?.internal) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }

        const dashboardUrl = ENV.isProduction
          ? `https://edge.symplirent.com/reports/${slug}`
          : `${API_CONFIG.baseURL}/reports/${slug}`;
        return (
          <Link
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            View Dashboard
          </Link>
        );
      },
      resizable: true,
    },
    {
      field: 'assignment_count',
      headerName: 'Active Assignments',
      filterable: false,
      width: 150,
      renderCell: (params: GridRenderCellParams<Location>) => {
        const count = assignmentCounts[params.row?.id || 0] || 0;
        return (
          <Chip
            label={`${count} assignments`}
            color={count > 0 ? 'primary' : 'default'}
            size="small"
            variant="outlined"
          />
        );
      },
      resizable: true,
    },

  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    createMutation.reset();
    updateMutation.reset();
  };

  const mutationError = createMutation.error || updateMutation.error;

  const handleSubmit = (data: CreateLocationData) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedLocation) {
      updateMutation.mutate({ id: selectedLocation.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (id: string) => {
    const location = locations.find((loc) => loc.id.toString() === id);
    if (location) {
      setSelectedLocation(location);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleOrganizationFilterChange = (_event: React.SyntheticEvent, org: { id: number; name: string } | null) => {
    if (org) {
      setSearchParams({ organization_id: org.id.toString() });
    } else {
      setSearchParams({});
    }
  };

  // Name filter is now server-side (debounced) — no client-side filtering needed

  const customActions = (params: GridRowParams) => [
    <GridActionsCellItem
      key="dashboard"
      icon={<DashboardIcon />}
      label="Location Dashboard"
      onClick={() => navigate(`/locations/${params.id}/dashboard`)}
      showInMenu={false}
    />,
    <GridActionsCellItem
      key="assignments"
      icon={<AssignmentIcon />}
      label="Manage Assignments"
      onClick={() => navigate(`/location_assignments?location_id=${params.id}`)}
      showInMenu={false}
    />,
  ];

  if (error) {
    return <Alert severity="error">Error loading locations</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Locations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Add Location
        </Button>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Autocomplete
          options={organizations}
          value={organizations.find(org => org.id.toString() === selectedOrganizationId) ?? null}
          onChange={handleOrganizationFilterChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Organization" />
          )}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label="Search by Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          sx={{ minWidth: 250 }}
        />
      </Box>

      <DataTable
        rows={locations}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        getRowId={(row: Location) => row.id.toString()}
        customActions={customActions}
        fullHeight
        getRowHeight={() => 'auto'}
        dataGridSx={{ '& .MuiDataGrid-row': { minHeight: '52px !important' } }}
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        pageSizeOptions={[25, 50, 100]}
      />

      <LocationForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedLocation}
        onExited={() => setSelectedLocation(undefined)}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert 
              severity={snackbar.severity} 
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
}; 